import { apiRequest } from "../services/chat.service";
import { MessageType, FlowType } from "../types/chat.type";

/**
 * チャット初期化レスポンス
 */
interface InitChatResponse {
  success: boolean;
  message?: string;
  data: {
    session_key: string;
    chatbot_id: string;
  };
}

/**
 * チャットボット設定レスポンス
 */
interface ChatbotConfigResponse {
  success: boolean;
  message?: string;
  data?: {
    design: any;
    config: any;
  };
  error?: string;
}

/**
 * チャット履歴アイテム
 */
interface ChatHistoryItem {
  message_id: string;
  text: string;
  isRobot: boolean;
  timestamp: string;
  avatar: string;
  should_display_feedback: boolean;
  feedback?: string;
  refer_links?: Array<{ title: string; url: string }>;
  doc_id?: string;
  isFlow?: boolean;
  flowEnded?: boolean;
  flowType?: FlowType;
  flowStepId?: string;
  choices?: Array<{ id: string; label: string }>;
  linkButtons?: Array<{ id: string; label: string; url: string }>;
}

/**
 * チャット履歴レスポンス
 */
interface ChatHistoryResponse {
  success: boolean;
  message?: string;
  data: ChatHistoryItem[];
  pagination: {
    page: number;
    size: number;
    total: number;
    has_more: boolean;
    first_message_id: string;
    last_message_id: string;
  };
}

/**
 * フィードバックレスポンス
 */
interface FeedbackResponse {
  success: boolean;
  message?: string;
  data?: ChatHistoryItem[];
}

/**
 * チャットリセットレスポンス
 */
interface ResetChatSessionResponse {
  success: boolean;
  message?: string;
  data: {
    session_key: string;
    chatbot_id: string;
  };
}

/**
 * チャットボット設定
 * @returns チャットボット設定レスポンス
 */
export async function fetchChatbotConfig(): Promise<ChatbotConfigResponse> {
  // 異常の場合はデフォルトテーマを使用する
  const result = await apiRequest<ChatbotConfigResponse>(
    "/api/v1/chatbot_config/",
    {
      method: "POST",
      body: {},
    },
  );
  return result;
}

/**
 * チャットする
 * @param params チャットパラメータ
 * @returns チャットストリーム
 */
export async function fetchChatAnswer(params: {
  message_id: string;
  question: string;
  message_type?: "text" | "flow_choice";
  flow_type?: FlowType;
  flow_step_id?: string;
  flow_button_id?: string;
}) {
  // ストリーミングで、異常の場合はHookで処理する
  const result = await apiRequest<ReadableStream>("/api/v1/do_chat/", {
    method: "POST",
    body: params,
    isStream: true,
  });
  return result;
}

/**
 * チャット履歴を取得する
 * @param page ページ番号
 * @param lastMessageId 一番上のメッセージID
 * @returns チャット履歴
 */
export async function fetchChatHistory(
  page: number = 0,
  lastMessageId?: string,
): Promise<{ messages: MessageType[]; hasMore: boolean }> {
  const requestBody: any = { page, size: 20 };
  if (page > 0 && lastMessageId) {
    requestBody.last_message_id = lastMessageId;
  }
  const result = await apiRequest<ChatHistoryResponse>("/api/v1/get_history/", {
    method: "POST",
    body: requestBody,
  });
  if (!result.success) {
    throw new Error("チャット履歴取得に失敗しました");
  }
  if (result.data && Array.isArray(result.data) && result.data.length > 0) {
    const messages: MessageType[] = result.data.map((msg) => ({
      id: msg.message_id,
      text: msg.text,
      sent: !msg.isRobot,
      timestamp: new Date(msg.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      avatar: msg.avatar,
      isRobot: msg.isRobot,
      shouldDisplayFeedback: msg.should_display_feedback,
      feedback: msg.feedback,
      referLinks: msg.refer_links,
      isFlow: msg.isFlow,
      flowData: msg.isFlow
        ? {
            flowType: msg.flowType!,
            flowStepId: msg.flowStepId ?? "",
            flowEnded: msg.flowEnded ?? false,
            choices: msg.choices || [],
            linkButtons: msg.linkButtons || [],
          }
        : undefined,
    }));
    return {
      messages,
      hasMore: result.pagination.has_more,
    };
  }
  return { messages: [], hasMore: false };
}

/**
 * チャット初期化
 * @param signal キャンセル信号
 * @returns チャット初期化レスポンス
 */
export async function initChat(
  signal?: AbortSignal,
): Promise<InitChatResponse> {
  const result = await apiRequest<InitChatResponse>("/api/v1/init_session/", {
    method: "POST",
    body: {},
    signal,
  });
  if (!result.success) {
    throw new Error(result.message || "初期化に失敗しました");
  }
  return result;
}

/**
 * フィードバック
 * @param params フィードバックパラメータ
 * @returns フィードバックレスポンス
 */
export async function sendFeedback(params: {
  feedback_flag: boolean;
  message_id: string;
}): Promise<FeedbackResponse> {
  const result = await apiRequest<FeedbackResponse>("/api/v1/feedback/", {
    method: "POST",
    body: params,
  });
  if (!result.success) {
    throw new Error(result.message || "フィードバックに失敗しました");
  }
  return result;
}

/**
 * 会話リセットをリセット
 * @param signal キャンセル信号
 * @returns 会話リセットレスポンス
 */
export async function resetChatSession(
  signal?: AbortSignal,
): Promise<ResetChatSessionResponse> {
  const result = await apiRequest<ResetChatSessionResponse>(
    "/api/v1/reset_chat/",
    {
      method: "POST",
      body: {},
      signal,
    },
  );
  if (!result.success) {
    throw new Error(result.message || "会話リセットに失敗しました");
  }
  return result;
}
